import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useConversations, useConversationMessages, useSendMessage, useMarkAsRead, useCreateConversation } from "@/hooks/useTeamComms";
import { usePresence } from "@/hooks/usePresence";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { ConversationList } from "@/components/controller/comms/ConversationList";
import { MessageThread } from "@/components/controller/comms/MessageThread";
import { NewConversationDialog } from "@/components/controller/comms/NewConversationDialog";
import { Button } from "@/components/ui/button";
import { PenSquare, ArrowLeft, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { OnlineIndicator } from "@/components/controller/comms/OnlineIndicator";
import { cn } from "@/lib/utils";

const Comms = () => {
  const { user } = useAuth();
  const { activeCompanyId } = useCompanySelector();
  const { data: conversations, isLoading: convLoading } = useConversations();
  const [activeConvoId, setActiveConvoId] = useState<string | null>(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const { isOnline, onlineUsers } = usePresence(activeCompanyId);

  const activeConvo = (conversations ?? []).find(c => c.id === activeConvoId);
  const { data: messages, isLoading: msgsLoading } = useConversationMessages(activeConvoId);
  const sendMessage = useSendMessage();
  const markAsRead = useMarkAsRead();
  const createConversation = useCreateConversation();

  const handleSend = (body: string, attachments?: import("@/hooks/useTeamComms").Attachment[]) => {
    if (!activeConvoId || !activeCompanyId) return;
    sendMessage.mutate({
      conversationId: activeConvoId,
      companyId: activeCompanyId,
      body: body || undefined,
      projectId: activeConvo?.project_id,
      attachments,
    });
  };

  const handleMarkRead = useCallback((messageIds: string[]) => {
    markAsRead.mutate({ messageIds });
  }, [markAsRead]);

  const handleCreateConvo = async (data: { title?: string; type: string; memberIds: string[]; projectId?: string }) => {
    if (!activeCompanyId) return;
    const result = await createConversation.mutateAsync({
      companyId: activeCompanyId,
      ...data,
    });
    setActiveConvoId(result.id);
  };

  // On mobile, show either list or thread
  const showThread = !!activeConvoId;

  return (
    <div className="flex h-[calc(100vh-8rem)] md:h-[calc(100vh-5rem)] rounded-xl border bg-card overflow-hidden">
      {/* Online users strip */}
      <div className="hidden lg:flex flex-col w-16 border-r bg-muted/30 items-center py-3 gap-2 overflow-y-auto">
        <p className="text-[9px] font-semibold uppercase text-muted-foreground mb-1">Online</p>
        {onlineUsers.filter(u => u.user_id !== user?.id).map(u => (
          <div key={u.user_id} className="relative" title={`${u.first_name} ${u.last_name}`}>
            <Avatar className="h-9 w-9">
              <AvatarImage src={u.profile_photo_url ?? undefined} />
              <AvatarFallback className="text-[10px]">{u.first_name[0]}{u.last_name[0]}</AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-0.5 -right-0.5">
              <OnlineIndicator isOnline={true} size="sm" />
            </div>
          </div>
        ))}
        {onlineUsers.filter(u => u.user_id !== user?.id).length === 0 && (
          <Users className="h-5 w-5 text-muted-foreground/40 mt-2" />
        )}
      </div>

      {/* Conversation list — left panel */}
      <div className={cn(
        "w-full md:w-80 border-r flex flex-col shrink-0",
        showThread ? "hidden md:flex" : "flex"
      )}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold">Messages</h2>
          <Button size="icon" variant="ghost" onClick={() => setNewDialogOpen(true)}>
            <PenSquare className="h-5 w-5" />
          </Button>
        </div>
        {convLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <ConversationList
            conversations={conversations ?? []}
            activeId={activeConvoId}
            onSelect={setActiveConvoId}
            currentUserId={user?.id ?? ""}
            isOnline={isOnline}
          />
        )}
      </div>

      {/* Message thread — right panel */}
      <div className={cn(
        "flex-1 flex flex-col",
        !showThread ? "hidden md:flex" : "flex"
      )}>
        {activeConvo ? (
          <>
            {/* Thread header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b">
              <Button
                size="icon"
                variant="ghost"
                className="md:hidden h-8 w-8"
                onClick={() => setActiveConvoId(null)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="relative">
                <Avatar className="h-9 w-9">
                  {(() => {
                    const other = activeConvo.members.find(m => m.user_id !== user?.id);
                    return (
                      <>
                        <AvatarImage src={other?.profile_photo_url ?? undefined} />
                        <AvatarFallback className="text-[10px]">
                          {other ? `${other.first_name[0]}${other.last_name[0]}` : "?"}
                        </AvatarFallback>
                      </>
                    );
                  })()}
                </Avatar>
                <div className="absolute -bottom-0.5 -right-0.5">
                  <OnlineIndicator
                    isOnline={activeConvo.members.some(m => m.user_id !== user?.id && isOnline(m.user_id))}
                    size="sm"
                  />
                </div>
              </div>
              <div>
                <p className="font-medium text-sm">
                  {activeConvo.title ?? (
                    activeConvo.members
                      .filter(m => m.user_id !== user?.id)
                      .map(m => `${m.first_name} ${m.last_name}`)
                      .join(", ") || "Conversation"
                  )}
                </p>
                {activeConvo.project_id && (
                  <p className="text-[11px] text-muted-foreground">Project linked</p>
                )}
              </div>
            </div>
            <MessageThread
              messages={messages ?? []}
              currentUserId={user?.id ?? ""}
              onSend={handleSend}
              onMarkRead={handleMarkRead}
              isLoading={msgsLoading}
              conversationMembers={activeConvo.members}
              conversationId={activeConvoId ?? undefined}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <div className="text-5xl mb-3">💬</div>
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm">or start a new one</p>
            </div>
          </div>
        )}
      </div>

      <NewConversationDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        onSubmit={handleCreateConvo}
        isOnline={isOnline}
      />
    </div>
  );
};

export default Comms;
